import { z } from 'zod';

import {
	conversationDetailSchema,
	conversationListItemSchema,
	conversationResultSchema,
	conversationStatusSchema,
} from '@/schemas/commons';
import type { ZodUseQuery } from '@/utils/zodReactQuery';

export const conversationsListResponse = z.object({
	items: z.array(conversationListItemSchema),
	pagination: z.object({
		page: z.number().int().positive(),
		pageSize: z.number().int().positive(),
		totalItems: z.number().int().nonnegative(),
		totalPages: z.number().int().nonnegative(),
	}),
	totals: z.object({
		costUsdFiltered: z.number().nonnegative(),
		costUsdPeriod: z.number().nonnegative(),
	}),
});

export type ConversationsListResponse = z.infer<typeof conversationsListResponse>;

export const conversationsParams = z
	.object({
		page: z.number().int().positive().optional(),
		pageSize: z.number().int().positive().optional(),
		moduleName: z.string().optional(),
		channel: z.string().optional(),
		country: z.string().optional(),
		period: z.enum(['today', '7d', '30d']).optional(),
		search: z.string().optional(),
		status: conversationStatusSchema.optional(),
		result: conversationResultSchema.optional(),
		// Exact AgendaPro company id, not a prefix — the operator pastes an id
		// they already have.
		companyId: z.string().optional(),
		// Both match against an array server-side: filtering by `agenda` also
		// returns conversations that touched `agenda` among other areas.
		area: z.string().optional(),
		alert: z.string().optional(),
	})
	.partial();

export type ConversationsParams = z.infer<typeof conversationsParams>;

export const conversationDetailResponse = conversationDetailSchema;
export type ConversationDetailResponse = z.infer<typeof conversationDetailResponse>;

/**
 * One chunk the RAG returned, as recorded by `back#SPEC-010`.
 *
 * `sourceId` is the article key the knowledge console routes on
 * (`/knowledge/articles/:sourceId`); `fileName` is the readable name. Both are
 * 1:1 with the article — measured 2026-08-11 against the live `support_v36`:
 * 381 unique values each, over 1950 chunks. `title` is display only: 380
 * distinct titles for those 381 articles, so it identifies nothing.
 *
 * `collection` is the resolved physical collection (`support_v36`), never the
 * alias — a re-ingest rotates every `chunkId`, so the alias would not say which
 * version actually answered.
 */
export const retrievedChunkSchema = z.object({
	collection: z.string().nullish(),
	chunkId: z.string().nullish(),
	sourceId: z.string().nullish(),
	fileName: z.string().nullish(),
	title: z.string().nullish(),
	score: z.number().nullish(),
	rank: z.number().int(),
});

export type RetrievedChunk = z.infer<typeof retrievedChunkSchema>;

export const agentEventSchema = z.object({
	id: z.number().int(),
	occurredAt: z.string(),
	eventType: z.string(),
	summary: z.string().nullish(),
	durationMs: z.number().int().nullish(),
	payload: z
		.object({ retrieved: z.array(retrievedChunkSchema).nullish() })
		.passthrough()
		.default({}),
	// `back#SPEC-010`. Nullish on purpose, not by oversight: every conversation
	// that predates migration 0013 has none, and the UI must be able to say
	// "no trace" instead of rendering an empty panel that reads as a bug.
	traceId: z.string().nullish(),
	spanId: z.string().nullish(),
	parentSpanId: z.string().nullish(),
	toolCallId: z.string().nullish(),
});

export type AgentEvent = z.infer<typeof agentEventSchema>;

export const conversationEventsResponse = z.object({
	threadId: z.string(),
	variants: z.record(z.string()),
	events: z.array(agentEventSchema),
});

export type ConversationEventsResponse = z.infer<typeof conversationEventsResponse>;

export type UseConversations = ZodUseQuery<ConversationsListResponse, ConversationsParams>;
export type UseConversation = ZodUseQuery<ConversationDetailResponse, { threadId: string }>;
export type UseConversationEvents = ZodUseQuery<ConversationEventsResponse, { threadId: string }>;
